import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type { IntlService } from 'ember-intl';
import type { LinkManagerService } from 'ember-link';
import type { NavigationTree } from 'walden-fast/components/sidebar';
import type FeatureFlagsService from 'walden-fast/services/feature-flags';
import type { FeatureFlag } from 'walden-fast/services/feature-flags';
import type SessionService from 'walden-fast/services/session';
import type UserService from 'walden-fast/services/user';
import type { Notification } from 'walden-fast/types/notification';
import type { User } from 'walden-fast/types/user';
import {
  SERVICE_NAMES,
  type ServiceName,
} from 'walden-fast/utils/constants/services';
import ClarityService from 'walden-fast/services/clarity';
import { POWERBI_MAP } from 'walden-fast/constants/power-bi';
import type PowerBiService from 'walden-fast/services/power-bi';
import type ImpersonateService from 'walden-fast/services/impersonate';
import type FilterService from 'walden-fast/services/filter';
import { matchLocales } from '@vdi/walden-shared/utils';
import config from 'walden-fast/config/environment';
import type FastApiService from 'walden-fast/services/fast-api';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type AppModel = Resolved<ReturnType<AppRoute['model']>>;

export default class AppRoute extends Route {
  @service('clarity') declare clarityService: ClarityService;
  @service('fast-api') declare fastApiService: FastApiService;
  @service('feature-flags') declare featureFlagsService: FeatureFlagsService;
  @service('filter') declare filterService: FilterService;
  @service('impersonate') declare impersonateService: ImpersonateService;
  @service('intl') declare intlService: IntlService;
  @service('link-manager') declare linkManagerService: LinkManagerService;
  @service('power-bi') declare powerBiService: PowerBiService;
  @service('router') declare routerService: RouterService;
  @service('session') declare sessionService: SessionService;
  @service('user') declare userService: UserService;

  async beforeModel() {
    return this.sessionService.redirectToLoginIfNotAuthenticated();
  }

  async model() {
    try {
      const user = await this.userService.getUser();

      if (this.userService.needsToImpersonate()) {
        await this.impersonateService.fetchAccounts();
      }

      await this.filterService.initialize();

      if (this.userService.user) {
        this.clarityService.identifyUser(this.userService.user.id);
      }

      let userLanguage =
        user.preferredLanguage ||
        this.intlService.primaryLocale?.toLowerCase() ||
        matchLocales({
          browserLocales: [...navigator.languages],
          supportedLocales: this.intlService.locales,
          defaultLocale: config.defaultLocale,
        })?.[0] ||
        'en-us';

      if (window.localStorage.getItem('language')) {
        this.userService.setPreferredLanguage(
          window.localStorage.getItem('language') as string,
        );
        userLanguage = window.localStorage.getItem('language') as string;
      }

      this.intlService.setLocale(userLanguage?.toLowerCase());

      if (!this.powerBiService.isTokenValid()) {
        await this.powerBiService.fetchTokenAndReports();
      }

      const notificationResponse =
        await this.fastApiService.get<Notification[]>('/notifications');

      return {
        availableAccounts: this.impersonateService.accounts || [],
        user,
        notifications: notificationResponse || [],
        language: userLanguage,
        // dont show the default fallback en
        availableLanguages: this.intlService.locales.filter(
          (locale) => locale !== 'en',
        ),
        navigationTree: await this.getNavigationTree(user),
      };
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  canAccessService = (serviceName: ServiceName) => {
    return this.userService.canAccessService(serviceName);
  };

  featureIsEnabled = async (flag: FeatureFlag) => {
    return this.featureFlagsService.isEnabled(flag);
  };

  hasRole = (user: User, roleName: string) => {
    return user.role.includes(roleName);
  };

  async getNavigationTree(user: User): Promise<NavigationTree> {
    return {
      groups: [
        {
          items: [
            {
              label: this.intlService.t('app.navigation.dashboard'),
              link: this.generateRoute('app.dashboard'),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
              ),
              featureEnabled: await this.featureIsEnabled('WALDEN_DASHBOARD'),
            },
          ],
        },
        {
          groupName: this.intlService.t('app.navigation.waldenView'),
          items: [
            {
              label: this.intlService.t(
                'app.navigation.waldenView.inboundOutbound',
              ),
              link: this.generateRoute('app.walden-view', ['inbound-outbound']),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
              ),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['inbound-outbound'].featureFlag,
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenView.inventory'),
              link: this.generateRoute('app.walden-view', ['inventory']),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
              ),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['inventory'].featureFlag,
              ),
            },
            {
              label: this.intlService.t(
                'app.navigation.waldenView.orderDetails',
              ),
              link: this.generateRoute('app.walden-view', ['order-details']),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
              ),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['order-details'].featureFlag,
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenView.masterData'),
              link: this.generateRoute('app.walden-view', ['master-data']),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
              ),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['master-data'].featureFlag,
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenView.transport'),
              link: this.generateRoute('app.walden-view', ['transport']),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_VIEW_TRANSPORT,
              ),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['transport'].featureFlag,
              ),
            },
            {
              label: this.intlService.t(
                'app.navigation.waldenView.orderTracking',
              ),
              link: this.generateRoute('app.walden-view', ['order-tracking']),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_VIEW_ORDER_TRACKING,
              ),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['order-tracking'].featureFlag,
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenView.order2Cash'),
              link: this.generateRoute('app.walden-view', ['order-to-cash']),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_VIEW_OTC,
              ),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['order-to-cash'].featureFlag,
              ),
            },
          ],
        },
        {
          groupName: this.intlService.t('app.navigation.services'),
          items: [
            {
              label: this.intlService.t('app.navigation.waldenGreen'),
              link: this.generateRoute('app.services.walden-green'),
              canAccessService:
                this.canAccessService(SERVICE_NAMES.WALDEN_GREEN_LEVEL1) ||
                this.canAccessService(SERVICE_NAMES.WALDEN_GREEN_LEVEL2) ||
                this.canAccessService(SERVICE_NAMES.WALDEN_GREEN_LEVEL3),

              featureEnabled: await this.featureIsEnabled(
                'WALDEN_SERVICE_GREEN',
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenMl'),
              link: this.generateRoute('app.services.walden-view-ref', [
                'walden-ml',
              ]),
              canAccessService: this.canAccessService(SERVICE_NAMES.WALDEN_ML),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['walden-ml'].featureFlag,
              ),
            },
            {
              label: this.intlService.t('app.navigation.orchestration'),
              link: this.generateRoute('app.services.walden-view-ref', [
                'walden-orchestration',
              ]),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_ORCHESTRATION,
              ),
              featureEnabled: await this.featureIsEnabled(
                POWERBI_MAP['walden-orchestration'].featureFlag,
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenTrace'),
              link: this.generateRoute('app.services.walden-trace', [
                'movianto-transport',
              ]),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_TRACE,
              ),
              featureEnabled: await this.featureIsEnabled(
                'WALDEN_SERVICE_TRACE',
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenBox'),
              link: this.generateRoute('app.services.walden-box'),
              canAccessService: this.canAccessService(SERVICE_NAMES.WALDEN_BOX),
              featureEnabled: await this.featureIsEnabled('WALDEN_SERVICE_BOX'),
            },
            {
              label: this.intlService.t('app.navigation.waldenConnect'),
              link: this.generateRoute('app.services.walden-connect'),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_CONNECT,
              ),
              featureEnabled: await this.featureIsEnabled(
                'WALDEN_SERVICE_CONNECT',
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenPulse'),
              link: this.generateRoute('app.services.walden-pulse'),
              canAccessService: this.canAccessService(
                SERVICE_NAMES.WALDEN_PULSE,
              ),
              featureEnabled: await this.featureIsEnabled(
                'WALDEN_SERVICE_PULSE',
              ),
            },
            {
              label: this.intlService.t('app.navigation.waldenVas'),
              link: this.generateRoute('app.services.walden-vas'),
              canAccessService: this.canAccessService(SERVICE_NAMES.WALDEN_VAS),
              featureEnabled: await this.featureIsEnabled('WALDEN_SERVICE_VAS'),
            },
          ],
        },
        ...(this.hasRole(user, 'AccountAdministrator') ||
        this.hasRole(user, 'WaldenAdministrator')
          ? [
              {
                groupName: this.intlService.t('app.navigation.manage'),
                items: [
                  {
                    label: this.intlService.t('app.navigation.organisation'),
                    link: this.generateRoute('app.manage.organisation'),
                    canAccessService: true,
                    featureEnabled: await this.featureIsEnabled(
                      'WALDEN_MANAGE_ORGANISATIONS',
                    ),
                  },
                  {
                    label: this.intlService.t('app.navigation.subscriptions'),
                    link: this.generateRoute('app.manage.subscriptions'),
                    canAccessService: true,
                    featureEnabled: await this.featureIsEnabled(
                      'WALDEN_MANAGE_SUBSCRIPTIONS',
                    ),
                  },
                ],
              },
            ]
          : []),
      ],
    };
  }

  generateRoute(routeName: string, models?: string[]) {
    return this.linkManagerService.createLink({ route: routeName, models });
  }
}
